import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentIcon from '@mui/icons-material/Payment';
import axios from 'axios';
import { API_URL } from '../../../common/Constants';
import Cookies from '../../../common/Cookies';
import StripePaymentModal from './StripePaymentModal';
import { useReseller } from '../../../contexts/ResellerContext';
import { getPlanFeaturesInOrder } from '../../../utils/planFeatureDisplay';
import StripePortalButton from './StripePortalButton';

const getAuthHeaders = () => ({
  Authorization: `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
});

const formatPrice = (cents) => {
  if (cents == null || cents === 0) return '$0';
  return `$${(cents / 100).toFixed(0)}`;
};

const CANCEL_REASON_OPTIONS = [
  { id: 'too_expensive', label: 'Too expensive / budget concerns' },
  { id: 'not_using', label: 'Not using the product enough' },
  { id: 'missing_features', label: 'Missing features I need' },
  { id: 'switching', label: 'Switching to another product' },
  { id: 'technical', label: 'Technical issues or reliability' },
  { id: 'other', label: 'Other', isOther: true }
];

const PlanInfoSection = ({ accountData, handlePlanChange, handleCycleChange, onRefreshAccount }) => {
  const { reseller } = useReseller();
  const showStripe = reseller == null || reseller.useStripeForBilling !== false;
  // Get initial billing cycle from accountData or default to 'monthly'
  const initialBillingCycle = accountData.billing_cycle || 'monthly';
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(null);
  const [planPrices, setPlanPrices] = useState({});
  // Helper to get plan name from value
  const getPlanName = (planValue) => {
    const found = plans.find((p) => p.value === planValue);
    return found ? found.name : planValue;
  };
  const [isUpdating, setIsUpdating] = useState(false);
  const [updatingPlan, setUpdatingPlan] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [stripeModalOpen, setStripeModalOpen] = useState(false);
  const [stripeModalPlan, setStripeModalPlan] = useState(null);
  const [stripeModalPlanName, setStripeModalPlanName] = useState('');
  // Pricing mode for display only - which prices to show (monthly vs annual). Does NOT change subscription.
  const [pricingMode, setPricingMode] = useState(initialBillingCycle);
  // Cancel plan flow: null | 'offer_lower' | 'confirm_cancel'
  const [cancelDialogStep, setCancelDialogStep] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  // Subscription set to cancel at period end (from GET payment/info)
  const [subscriptionCancelStatus, setSubscriptionCancelStatus] = useState({ cancelAtPeriodEnd: false, periodEndFormatted: null });
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [billingHistoryOpen, setBillingHistoryOpen] = useState(false);
  const [billingHistoryLoading, setBillingHistoryLoading] = useState(false);
  const [billingHistoryRows, setBillingHistoryRows] = useState([]);
  const [billingHistoryError, setBillingHistoryError] = useState(null);
  const [cancelReasonId, setCancelReasonId] = useState('');
  const [cancelReasonOther, setCancelReasonOther] = useState('');

  const getResolvedCancellationReason = () => {
    const opt = CANCEL_REASON_OPTIONS.find((r) => r.id === cancelReasonId);
    if (!opt) return '';
    if (opt.isOther) {
      const detail = (cancelReasonOther || '').trim();
      return detail ? `Other: ${detail}` : '';
    }
    return opt.label;
  };

  const canSubmitCancelConfirm = () => {
    if (!cancelReasonId) return false;
    const opt = CANCEL_REASON_OPTIONS.find((r) => r.id === cancelReasonId);
    if (opt?.isOther) return (cancelReasonOther || '').trim().length > 0;
    return true;
  };

  const openBillingHistory = () => {
    setBillingHistoryOpen(true);
    setBillingHistoryLoading(true);
    setBillingHistoryError(null);
    axios
      .get(`${API_URL}/payment/billing-history`, { headers: getAuthHeaders() })
      .then((res) => {
        if (res.data.success) {
          setBillingHistoryRows(Array.isArray(res.data.invoices) ? res.data.invoices : []);
        } else {
          setBillingHistoryError(res.data.message || 'Failed to load billing history');
        }
      })
      .catch((err) => {
        setBillingHistoryError(
          err.response?.data?.message || err.message || 'Failed to load billing history'
        );
      })
      .finally(() => setBillingHistoryLoading(false));
  };

  const fetchPaymentInfo = () => {
    const p = accountData.currentPlan;
    if (!p || p === 'B' || !['S', 'P', 'U'].includes(p)) return;
    axios.get(`${API_URL}/payment/info`, { headers: getAuthHeaders() })
      .then((res) => {
        if (res.data.success && res.data.subscription) {
          setSubscriptionCancelStatus({
            cancelAtPeriodEnd: !!res.data.subscription.cancelAtPeriodEnd,
            periodEndFormatted: res.data.subscription.periodEndFormatted || null
          });
        } else {
          setSubscriptionCancelStatus({ cancelAtPeriodEnd: false, periodEndFormatted: null });
        }
      })
      .catch(() => setSubscriptionCancelStatus({ cancelAtPeriodEnd: false, periodEndFormatted: null }));
  };

  useEffect(() => {
    fetchPaymentInfo();
  }, [accountData.currentPlan]);

  // Toggle pricing display - only changes which prices are shown, does NOT update subscription
  const handlePricingModeToggle = (newMode) => {
    if (pricingMode === newMode) return;
    setPricingMode(newMode);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const hasPaidSubscription = () => {
    const p = accountData.currentPlan;
    return p && p !== 'B' && ['S', 'P', 'U'].includes(p);
  };

  const openCancelDialog = () => {
    setCancelReasonId('');
    setCancelReasonOther('');
    setCancelDialogStep('offer_lower');
  };
  const closeCancelDialog = () => {
    setCancelDialogStep(null);
    setCancelLoading(false);
    setCancelReasonId('');
    setCancelReasonOther('');
  };

  const handleConfirmCancelPlan = async () => {
    const cancellationReason = getResolvedCancellationReason();
    if (!cancellationReason) {
      showSnackbar('Please select a reason for cancelling.', 'warning');
      return;
    }
    setCancelLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/payment/cancel-subscription`,
        { cancellation_reason: cancellationReason },
        { headers: getAuthHeaders() }
      );
      if (res.data.success) {
        const endDate = res.data.periodEndFormatted || 'end of your billing period';
        showSnackbar(`Subscription cancelled. Your account will remain active until ${endDate}.`, 'success');
        closeCancelDialog();
        onRefreshAccount?.();
        fetchPaymentInfo();
      } else {
        showSnackbar(res.data.message || 'Failed to cancel subscription', 'error');
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || err.message || 'Failed to cancel subscription', 'error');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleRestoreSubscription = async () => {
    setRestoreLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/payment/restore-subscription`,
        {},
        { headers: getAuthHeaders() }
      );
      if (res.data.success) {
        showSnackbar(res.data.message || 'Subscription restored.', 'success');
        fetchPaymentInfo();
        onRefreshAccount?.();
      } else {
        showSnackbar(res.data.message || 'Failed to restore subscription', 'error');
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || err.message || 'Failed to restore subscription', 'error');
    } finally {
      setRestoreLoading(false);
    }
  };

  const handlePlanChangeWithAPI = async (newPlan) => {
    const planName = getPlanName(newPlan);
    // Beta plan: update directly without Stripe
    if (newPlan === 'B') {
      setIsUpdating(true);
      setUpdatingPlan(newPlan);
      try {
        const response = await axios.post(`${API_URL}/account/update-plan`, {
          plan: newPlan,
          billing_cycle: pricingMode
        }, {
          headers: {
            'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
          }
        });
        if (response.data.success) {
          handlePlanChange(newPlan, pricingMode);
          showSnackbar(`✅ Plan updated to ${planName} successfully!`, 'success');
        } else {
          showSnackbar('❌ Failed to update plan: ' + response.data.message, 'error');
        }
      } catch (error) {
        showSnackbar('❌ Error updating plan', 'error');
      } finally {
        setIsUpdating(false);
        setUpdatingPlan(null);
      }
      return;
    }
    // Paid plans: open Stripe Checkout modal (only when Stripe billing is enabled)
    if (!showStripe) {
      showSnackbar('Plan changes are managed by your administrator. Please contact them for billing.', 'info');
      return;
    }
    setStripeModalPlan(newPlan);
    setStripeModalPlanName(planName);
    setStripeModalOpen(true);
  };

  // Fetch plans from database
  useEffect(() => {
    axios
      .get(`${API_URL}/account/plans`, { headers: getAuthHeaders() })
      .then((res) => {
        if (res.data.success && Array.isArray(res.data.plans)) {
          setPlansError(null);
          setPlans(res.data.plans);
          const prices = {};
          res.data.plans.forEach((p) => {
            prices[p.value] = {
              monthly: formatPrice(p.monthlyPriceCents),
              annual: formatPrice(p.annualPriceCents)
            };
          });
          setPlanPrices(prices);
        } else {
          setPlansError(res.data?.message || 'Unexpected response format');
        }
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || err?.message || 'Failed to fetch plans';
        setPlansError(msg);
        console.error('Failed to fetch plans:', err?.response?.data || err);
      })
      .finally(() => setPlansLoading(false));
  }, []);

  // Sync pricingMode with accountData.billing_cycle if it changes
  useEffect(() => {
    if (accountData.billing_cycle && accountData.billing_cycle !== pricingMode) {
      setPricingMode(accountData.billing_cycle);
    }
  }, [accountData.billing_cycle]);
  if (plansLoading) {
    return (
      <Paper sx={{ p: 3, mt: 2, pb: 8, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (plans.length === 0) {
    return (
      <Paper sx={{ p: 3, mt: 2, pb: 8 }}>
        <Typography color="text.secondary">
          {plansError || 'No plans available. Please try again later.'}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 2, pb: 8 }}>
      {showStripe && (
      <StripePaymentModal
        open={stripeModalOpen}
        onClose={() => setStripeModalOpen(false)}
        plan={stripeModalPlan}
        planName={stripeModalPlanName}
        billingCycle={pricingMode}
        onSuccess={() => {
          handlePlanChange(stripeModalPlan, pricingMode);
          onRefreshAccount?.();
          showSnackbar(`✅ Plan updated to ${stripeModalPlanName} successfully!`, 'success');
        }}
      />
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" sx={{ mr: 1 }}>Plan Information</Typography>
        <Typography variant="h6" sx={{ mr: 1 }}>- Current Plan: <strong>{getPlanName(accountData.currentPlan)}</strong></Typography>
        <Chip
          label={accountData.planStatus}
          color={accountData.planStatus === 'Active' ? 'success' : 'default'}
          size="small"
          sx={{ ml: 1 }}
        />
        {/* Show billing cycle */}
        {accountData.billing_cycle && (
          <Chip
            label={accountData.billing_cycle.charAt(0).toUpperCase() + accountData.billing_cycle.slice(1) + ' Billing'}
            color="info"
            size="small"
            sx={{ ml: 1 }}
          />
        )}
        {showStripe && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ReceiptLongIcon sx={{ fontSize: 18 }} />}
            onClick={openBillingHistory}
            sx={{ ml: { xs: 0, sm: 1 }, fontWeight: 600, textTransform: 'none' }}
          >
            Billing history
          </Button>
        )}
        {showStripe && hasPaidSubscription() && (
          <StripePortalButton
            label="Payment & billing"
            size="small"
            startIcon={<PaymentIcon sx={{ fontSize: 18 }} />}
            onError={(msg) => showSnackbar(msg || 'Failed to open Stripe billing portal', 'error')}
            sx={{ fontWeight: 600, textTransform: 'none' }}
          />
        )}
      </Box>

      <Dialog
        open={billingHistoryOpen}
        onClose={() => setBillingHistoryOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          Billing history
          <IconButton aria-label="Close" onClick={() => setBillingHistoryOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {billingHistoryLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!billingHistoryLoading && billingHistoryError && (
            <Typography color="error">{billingHistoryError}</Typography>
          )}
          {!billingHistoryLoading && !billingHistoryError && billingHistoryRows.length === 0 && (
            <Typography color="text.secondary">
              No invoices yet. Invoices appear here after your first payment.
            </Typography>
          )}
          {!billingHistoryLoading && !billingHistoryError && billingHistoryRows.length > 0 && (
            <TableContainer sx={{ maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Invoice</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Receipt</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {billingHistoryRows.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell>{inv.createdFormatted || '—'}</TableCell>
                      <TableCell>{inv.number || inv.id.slice(0, 12) + '…'}</TableCell>
                      <TableCell align="right">
                        {formatPrice(inv.amountPaid != null ? inv.amountPaid : inv.total)}
                      </TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>
                        {inv.status || '—'}
                      </TableCell>
                      <TableCell align="right">
                        {inv.hostedInvoiceUrl && (
                          <Link href={inv.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
                            View
                          </Link>
                        )}
                        {inv.hostedInvoiceUrl && inv.invoicePdf ? ' · ' : null}
                        {inv.invoicePdf && (
                          <Link href={inv.invoicePdf} target="_blank" rel="noopener noreferrer">
                            PDF
                          </Link>
                        )}
                        {!inv.hostedInvoiceUrl && !inv.invoicePdf && '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBillingHistoryOpen(false)} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Typography variant="subtitle1" gutterBottom sx={{ mt: 3, mb: 2, fontSize: 24, fontWeight: 400, textAlign: 'center' }}>
        Available Plans
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Button
          variant={pricingMode === 'monthly' ? 'contained' : 'outlined'}
          color="primary"
          sx={{ mr: 1, fontWeight: 700 }}
          onClick={() => handlePricingModeToggle('monthly')}
        >
          Monthly Pricing
        </Button>
        <Button
          variant={pricingMode === 'annual' ? 'contained' : 'outlined'}
          color="primary"
          sx={{ fontWeight: 700 }}
          onClick={() => handlePricingModeToggle('annual')}
        >
          Annual Pricing
        </Button>
      </Box>
      <Grid container spacing={2} justifyContent="center">
        {plans
          .filter((plan) => {
            if (plan.value === 'B') {
              return accountData.currentPlan === 'B';
            }
            return true;
          })
          .map((plan) => {
          // Current plan must match both plan tier AND billing cycle (monthly vs annual)
          const isCurrent = accountData.currentPlan === plan.value &&
            (accountData.billing_cycle || 'monthly') === pricingMode;
          const isBeta = plan.value === 'B';
          const isPaidPlan = !isBeta && ['S', 'P', 'U'].includes(plan.value);
          const stripeDisabledForPaid = !showStripe && isPaidPlan;
          return (
            <Grid item xs={12} sm={3} key={plan.value} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
              <Card
                variant="outlined"
                sx={{
                  position: 'relative',
                  minHeight: 420,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  borderColor: isCurrent ? 'primary.main' : 'divider',
                  boxShadow: isCurrent ? 3 : 0,
                  width: '290px',
                  pt: plan.value === 'B' ? 2 : 2,
                  pb: 2, // Always use pb:2 for all cards for bottom alignment
                  mt: 0,
                  mb: plan.value === 'B' ? -4 : 0,
                  background: plan.value === 'B' ? (theme) => theme.palette.primary.main : (plan.highlight ? 'linear-gradient(180deg, #f5fbff 80%, #fff 100%)' : '#fff'),
                  borderTop: plan.highlight && plan.value !== 'B' ? (theme) => `5px solid ${theme.palette.primary.main}` : undefined,
                  zIndex: plan.value === 'B' ? 2 : 1,
                  boxShadow: plan.value === 'B' ? 6 : (isCurrent ? 3 : 0),
                  overflow: 'visible',
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 0, pt: 5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {/* Plan Name (Free Trial badge) */}
                  {plan.value === 'B' ? (
                    <Box sx={{ mb: 1, width: '100%', display: 'flex', justifyContent: 'center' }}>
                      <Box sx={{ background: 'red', color: '#fff', borderRadius: 1, px: 2.5, py: 0.5, fontWeight: 700, fontSize: 18, letterSpacing: 1, boxShadow: 2, display: 'inline-block' }}>
                        Free Trial
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="h5" component="div" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5, mt: 0 }}>
                      {plan.name}
                      {isCurrent && (
                        <CheckCircleOutlineIcon color="primary" sx={{ ml: 1, verticalAlign: 'middle', fontSize: 24 }} />
                      )}
                    </Typography>
                  )}
                  {/* Price (dynamic by pricingMode): tiles always show monthly rate */}
                  <Typography variant="h3" sx={{ fontWeight: 700, color: plan.value === 'B' ? '#fff' : 'primary.main', mb: 0.5, mt: 0 }}>
                    {(planPrices[plan.value] && planPrices[plan.value][pricingMode]) || '$0'}
                    {plan.value === 'B' ? '' : (
                      <Typography variant="h6" component="span" sx={{ fontWeight: 400, ml: 1, fontSize: 18, color: 'primary.main' }}>/mo</Typography>
                    )}
                  </Typography>
                  {/* Beta price note */}
                  {plan.priceNote && (
                    <Typography variant="subtitle2" sx={{ color: plan.value === 'B' ? '#fff' : (plan.highlight ? 'primary.main' : 'text.secondary'), fontWeight: plan.highlight ? 700 : 400, mb: 1, mt: 0 }}>{plan.priceNote}</Typography>
                  )}
                  {/* Prorated + month-end renewal when switching from Free to paid (monthly) */}
                  {plan.value !== 'B' && !isCurrent && accountData.currentPlan === 'B' && pricingMode === 'monthly' && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                      Prorated for days left this month, then renews at the end of each month
                    </Typography>
                  )}
                  {/* Description (e.g. 'No Credit Card Required') */}
                  {plan.description && (
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: plan.value === 'B' ? '#fff' : 'text.secondary' }}>{plan.description}</Typography>
                  )}
                  {/* Feature List in order: Conversations, Overage, Chatbot Agents, Live Chat Seats, Storage, Integrated Tasks */}
                  <Box sx={{ width: '100%', mt: 1 }}>
                    {getPlanFeaturesInOrder(plan).map((feature, idx, arr) => (
                      <React.Fragment key={idx}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.5 }}>
                          <Typography variant="body1" sx={{ color: plan.value === 'B' ? '#fff' : '#c9c9c9', fontWeight: 500, fontSize: 16 }}>{feature}</Typography>
                        </Box>
                        {idx < arr.length - 1 && (
                          <Box sx={{ width: '85%', height: 2, bgcolor: plan.value === 'B' ? '#fff' : '#c9c9c9', opacity: 0.5, mx: 'auto', my: 1, borderRadius: 1 }} />
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                </CardContent>
                <CardActions sx={{ mt: 'auto', width: '100%', px: 2, pb: 1, justifyContent: 'center' }}>
                  <Button
                    fullWidth
                    variant={isCurrent ? "outlined" : "contained"}
                    color={plan.value === 'B' ? 'inherit' : 'primary'}
                    disabled={isCurrent || isUpdating || stripeDisabledForPaid}
                    onClick={() => handlePlanChangeWithAPI(plan.value)}
                    startIcon={updatingPlan === plan.value ? <CircularProgress size={20} color={plan.value === 'B' ? 'inherit' : 'primary'} /> : null}
                    sx={{
                      fontWeight: 700,
                      fontSize: 16,
                      py: 1,
                      bgcolor: plan.value === 'B' ? '#fff' : undefined,
                      color: plan.value === 'B' ? 'primary.main' : undefined,
                      border: plan.value === 'B' ? '2px solid #fff' : undefined,
                      '&:hover': plan.value === 'B' ? { bgcolor: '#e6e6e6', color: 'primary.main' } : undefined
                    }}
                  >
                    {updatingPlan === plan.value ? 'Updating...' :
                      stripeDisabledForPaid ? 'Contact administrator for billing' :
                      isCurrent ? 'Current Plan' : 'Switch to This Plan'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {showStripe && hasPaidSubscription() && (
        <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          {subscriptionCancelStatus.cancelAtPeriodEnd ? (
            <>
              <Typography variant="body2" color="text.secondary">
                Your plan is set to cancel{subscriptionCancelStatus.periodEndFormatted ? ` on ${subscriptionCancelStatus.periodEndFormatted}` : ''}. You can restore it until then.
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={restoreLoading ? <CircularProgress size={18} color="inherit" /> : <RestoreIcon sx={{ fontSize: 18 }} />}
                onClick={handleRestoreSubscription}
                disabled={restoreLoading}
                sx={{ fontWeight: 600, textTransform: 'none' }}
              >
                Restore subscription
              </Button>
            </>
          ) : (
            <Button
              variant="text"
              color="inherit"
              size="small"
              startIcon={<CancelOutlinedIcon sx={{ fontSize: 18 }} />}
              onClick={openCancelDialog}
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': { color: 'text.primary', bgcolor: 'action.hover' }
              }}
            >
              Cancel plan
            </Button>
          )}
        </Box>
      )}

      {/* Cancel plan: step 1 – offer to switch to lower plan */}
      <Dialog open={cancelDialogStep === 'offer_lower'} onClose={closeCancelDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberOutlinedIcon color="warning" />
            Cancel your plan?
          </Box>
          <IconButton aria-label="Close" onClick={closeCancelDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel? You can switch to a lower plan instead and keep your account with reduced features.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            Would you like to switch to a lower plan, or cancel your subscription?
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}
        >
          <Button onClick={closeCancelDialog} color="primary" variant="contained">
            Switch to a lower plan
          </Button>
          <Button
            onClick={() => setCancelDialogStep('confirm_cancel')}
            variant="text"
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
              boxShadow: 'none',
              border: 'none',
              '&:hover': {
                bgcolor: 'action.hover',
                border: 'none'
              }
            }}
          >
            No, cancel my subscription
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel plan: step 2 – confirm and explain end-of-period access */}
      <Dialog open={cancelDialogStep === 'confirm_cancel'} onClose={closeCancelDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          Confirm cancellation
          <IconButton aria-label="Close" onClick={closeCancelDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your account will remain active until the end of your current billing period (end of month). You will not be charged again.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            Do you still want to cancel your plan?
          </DialogContentText>
          <FormControl component="fieldset" variant="standard" sx={{ mt: 2, width: '100%' }} disabled={cancelLoading}>
            <FormLabel component="legend" sx={{ mb: 1 }}>
              Why are you cancelling?
            </FormLabel>
            <RadioGroup
              value={cancelReasonId}
              onChange={(e) => setCancelReasonId(e.target.value)}
              name="cancel-reason"
            >
              {CANCEL_REASON_OPTIONS.map((opt) => (
                <FormControlLabel
                  key={opt.id}
                  value={opt.id}
                  control={<Radio size="small" />}
                  label={opt.label}
                />
              ))}
            </RadioGroup>
            {cancelReasonId === 'other' && (
              <TextField
                fullWidth
                multiline
                minRows={3}
                placeholder="Please describe your reason (required)"
                value={cancelReasonOther}
                onChange={(e) => setCancelReasonOther(e.target.value)}
                sx={{ mt: 1 }}
                inputProps={{ maxLength: 4000 }}
              />
            )}
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelDialogStep('offer_lower')} disabled={cancelLoading}>
            Go back
          </Button>
          <Button
            onClick={handleConfirmCancelPlan}
            color="error"
            variant="contained"
            disabled={cancelLoading || !canSubmitCancelConfirm()}
            startIcon={cancelLoading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {cancelLoading ? 'Cancelling...' : 'Yes, cancel my plan'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default PlanInfoSection;